import CardCommon from '@/pages/opinion/components/CardCommon';
import ProgressContext from '@/pages/opinion/components/ProgressContext';
import React, { useCallback } from 'react';
import { useMemo } from 'react';
import { Button } from 'tea-component';
import { useIntl } from 'umi';
import { getSteamReviewRate, RenderKeyType } from './useDealwithSteam';
import { percentageFormat } from '@/pages/opinion/utils/value';
import {
  arrayToObj,
  getPercentageInfo,
  getPercentageValue,
} from '@/pages/opinion/utils';
import { NO_DATE } from '@/pages/opinion/constants';
import { platformTitleMap } from '../constants';
import steamPositive from '@op/assets/svg/steam_positive.svg';
import steamNegative from '@op/assets/svg/steam_negative.svg';
import { dealwithSteamSelectedData } from '@op/utils/data';

export default function useDealwithPlatform({
  data,
  loading,
  setTime,
  show = {
    metacritic: true,
    opencritic: true,
    steam: true,
  },
  isToday = false,
  steamReviews = [],
}: {
  data: {
    metacritic: null | Array<any>;
    opencritic: null | Array<any>;
    steam: null | Array<any>;
  };
  loading?: boolean;
  setTime?: (value: string, key: 'metacritic' | 'opencritic' | 'steam') => void;
  show?: {
    metacritic: boolean;
    opencritic: boolean;
    steam: boolean;
  };
  isToday?: boolean;
  steamReviews?: Array<{
    key: 'recommend' | 'non_recommend';
    value: number;
  }>;
}) {
  const { formatMessage } = useIntl();

  const { metacritic, opencritic } = useMemo(() => {
    const { metacritic, opencritic }: any = data || {};
    const metacriticSort = ['user_score', 'meta_score'];
    const opencriticSort = ['top_critic_average', 'critics_recommend'];

    const metacriticOptions = metacritic;
    const opencriticOptions = opencritic;

    const metacriticResult = metacriticOptions?.sort(
      (a: any, b: any) =>
        metacriticSort.indexOf(a.key) - metacriticOptions.indexOf(b.key),
    );

    const opencriticResult = opencriticOptions?.sort(
      (a: any, b: any) =>
        opencriticSort.indexOf(a.key) - opencriticSort.indexOf(b.key),
    );
    return {
      metacritic: metacriticResult,
      opencritic: opencriticResult,
    };
  }, [data]);

  const getNodataElement = useCallback(
    (data: any, key: 'metacritic' | 'opencritic' | 'steam') => {
      if (!data || data?.length === 0) {
        return (
          <div className="game-store-card-tips">
            {formatMessage({
              id: 'The score for the selected period has not been crawled yet.',
            })}
          </div>
        );
      }
      const info = data.find((item: any) => item);

      const { is_last_time_data, has_date_time } = info || {};
      // 如果为false，说明不是当天数据
      if (is_last_time_data && !show[key] && !isToday) {
        return (
          <div className="game-store-card-tips">
            {formatMessage({
              id: 'The score for the selected period has not been crawled yet.',
            })}{' '}
            <Button
              type="link"
              onClick={() => {
                setTime?.(has_date_time, key);
              }}
            >
              {formatMessage({
                id: 'Click to view recent score.',
              })}
            </Button>
          </div>
        );
      }

      return null;
    },
    [formatMessage, show, isToday, setTime],
  );

  const getElementList = useCallback(
    (data: any, key: 'metacritic' | 'opencritic') => {
      const noDataElement = getNodataElement(data, key);
      if (noDataElement !== null) {
        return noDataElement;
      }

      return data?.map(({ review, score, key }: any) => (
        <ProgressContext
          key={key}
          percent={key === 'user_score' ? score * 10 : score}
          circle
          sentiment
          strokeWidth={10}
          sizeWidth={120}
          text={score + (key === 'critics_recommend' ? '%' : '')}
          title={formatMessage({ id: platformTitleMap[key] || NO_DATE.EMPTY })}
          reviewNum={review}
        />
      ));
    },
    [formatMessage, show, isToday, getNodataElement],
  );

  const metacriticElement = useMemo(() => {
    return (
      <CardCommon
        className="context-item-progress-card"
        contentCard
        hasEmptyImg={false}
        hasEmptyTitle={false}
        hasEmptyDesc={false}
        emptyHeight={220}
        loading={loading}
        loadingHeight={220}
      >
        {getElementList(metacritic, 'metacritic')}
      </CardCommon>
    );
  }, [metacritic, getElementList, loading]);

  const opencriticElement = useMemo(() => {
    return (
      <CardCommon
        className="context-item-progress-card"
        contentCard
        hasEmptyImg={false}
        hasEmptyTitle={false}
        hasEmptyDesc={false}
        emptyHeight={220}
        loading={loading}
        loadingHeight={220}
      >
        {getElementList(opencritic, 'opencritic')}
      </CardCommon>
    );
  }, [opencritic, getElementList, loading]);

  function getOneIconAndProgressElement(
    data: { steam: Array<any> | null },
    key: RenderKeyType,
    text: 'All Reviews' | 'Added Reviews' | 'Recent Reviews' = 'All Reviews',
  ) {
    const noDataElement = getNodataElement(data?.steam || [], 'steam');
    if (noDataElement !== null) {
      return noDataElement;
    }
    const { icon, name, review, score } = getSteamReviewRate(data, key);

    const valueText = percentageFormat(score * 100, '-', 0);
 

    return (
      <CardCommon
        className="context-item-progress-card"
        contentCard
        hasEmptyImg={false}
        hasEmptyTitle={false}
        hasEmptyDesc={false}
        emptyHeight={220}
        loading={loading}
        loadingHeight={220}
      >
        <>
          <ProgressContext
            title={formatMessage({
              id: review === 0 ? NO_DATE.SIMPLE : name || NO_DATE.EMPTY,
            })}
            iconType={review === 0 ? null : icon}
          />
          <ProgressContext
            percent={score * 100}
            text={valueText}
            circle
            sentiment
            strokeColor="#29B569"
            title={formatMessage({ id: text })}
            reviewNum={review}
            bubbleTips={`${formatMessage({ id: 'Positive%' })}: ${valueText}`}
          />
        </>
      </CardCommon>
    );
  }

  const steamElement = useMemo(() => {
    return getOneIconAndProgressElement(data, 'all_reviews', 'All Reviews');
  }, [data, getNodataElement]);

  const steamAddedReviewElement = useMemo(() => {
    const steam = data?.steam || [];
    const realData = dealwithSteamSelectedData(steam);
    return getOneIconAndProgressElement(
      {steam: realData},
      'select_all_review',
      'Added Reviews',
    );
  }, [data, getNodataElement]);

  const steamReviewElement = useMemo(() => {
    const total = steamReviews?.reduce(
      (pre: any, cur: { value: any }) => cur.value + pre,
      0,
    );
    const reviewObj = arrayToObj(steamReviews, 'key');
    return (
      <CardCommon
        className="context-item-progress-card"
        contentCard
        hasEmptyImg={false}
        hasEmptyTitle={false}
        hasEmptyDesc={false}
        emptyHeight={220}
        loading={loading}
        loadingHeight={220}
      >
        <>
          {[
            {
              color: '#29B569',
              icon: steamPositive,
              text: 'Positive',
              key: 'recommend',
            },
            {
              color: '#EF5858',
              icon: steamNegative,
              text: 'Negative',
              key: 'non_recommend',
            },
          ].map(({ color, text, key, icon }) => {
            const { value } = reviewObj[key] || {};
            const showText = getPercentageInfo(value || 0, total, 0, '0%');
            const percent = getPercentageValue(value || 0, total, 4);

            return (
              <ProgressContext
                key={key}
                percent={percent * 100}
                circle
                sentiment
                strokeWidth={10}
                sizeWidth={120}
                strokeColor={color}
                text={showText}
                title={
                  <>
                    <img
                      style={{
                        width: 20,
                        height: 20,
                        marginRight: 5,
                        verticalAlign: 'sub',
                      }}
                      src={icon}
                    ></img>
                    {formatMessage({ id: text })}
                  </>
                }
                reviewNum={value}
              />
            );
          })}
        </>
      </CardCommon>
    );
  }, [steamReviews, formatMessage]);

  return {
    metacriticElement,
    opencriticElement,
    metacritic,
    opencritic,
    steamElement,
    steamReviewElement,
    steamAddedReviewElement,
  };
}
